import { ICSS } from "../../../interfaces/css";
import { ILayout } from "../../../interfaces/layout";
import style from "./style.css";
import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";

export interface IBottomSheetLayout extends ILayout {
  type_id: "BOTTOM_SHEET";
  ui: {
    content: {};
    css: {
      backDrop?: ICSS;
      container: ICSS;
      dash?: ICSS;
    };
  };
}

interface IProps extends IBottomSheetLayout {
  children: any;
  onClose: () => void;
  isActive: boolean;
  startClose: boolean;
}

export const BottomSheet = (props: IProps) => {
  const { content, css } = props.ui;
  const [isOpen, setOpen] = useState(true);
  const [isDragging, toggleDragging] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const startPoint = useRef<number>(0);
  const lastPoint = useRef<number>(0);
  const currentPoint = useRef<number>(0);
  const currentTransform = useRef<number>(0);
  const animationRef = useRef<number>();
  const scrolled = useRef<boolean>(false);

  useEffect(() => {
    if (props.startClose) close();
  }, [props.startClose]);

  const close = () => {
    setOpen(false);
  };

  const prevent = (e: Event) => {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
  };

  const touchStart = (e: TouchEvent) => {
    startPoint.current =
      lastPoint.current =
      currentPoint.current =
        e.touches[0].clientY;
    sheetRef.current.style.transition = `none 0ms ease-in`;
  };

  const touchMove = (e: TouchEvent) => {
    if (scrolled.current) return;
    if (sheetRef.current.scrollTop !== 0) {
      scrolled.current = true;
      e.stopPropagation();
      stopAnimation();
      return;
    }

    if (e.touches[0].clientY <= startPoint.current) {
      scrolled.current = true;
      e.stopPropagation();
      if (isDragging) toggleDragging(false);
      stopAnimation();
      return;
    } else {
      prevent(e);
      lastPoint.current = currentPoint.current;
      currentPoint.current = e.touches[0].clientY;
      if (!isDragging) {
        toggleDragging(true);
        startAnimation();
      }
    }
  };

  const touchEnd = (e: TouchEvent) => {
    if (!isDragging) {
      scrolled.current = false;
      startPoint.current = null;
      lastPoint.current = null;
      currentPoint.current = null;
      currentTransform.current = 0;
      return;
    }

    toggleDragging(false);
    stopAnimation();
    const diff = lastPoint.current - startPoint.current;

    if (Math.abs(diff) < 60) {
      sheetRef.current.style.transform = "translateY(0px)";
      sheetRef.current.style.transition = "transform 300ms ease-out";
    } else {
      close();
    }

    startPoint.current = null;
    lastPoint.current = null;
    currentPoint.current = null;
    currentTransform.current = 0;
    scrolled.current = false;
    prevent(e);
  };

  const startAnimation = () => {
    currentTransform.current =
      currentTransform.current + currentPoint.current - lastPoint.current;
    if (currentTransform.current < 0) {
      currentTransform.current = 0;
    }
    sheetRef.current.style.transform = `translate3d(0, ${currentTransform.current}px, 0)`;
    animationRef.current = requestAnimationFrame(startAnimation);
    lastPoint.current = currentPoint.current;
  };

  const stopAnimation = () => {
    cancelAnimationFrame(animationRef.current);
  };

  const onAnimeEnd = (e: AnimationEvent) => {
    if (!isOpen) props.onClose();
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div

      className={`${style.sheetContainer} ${isOpen ? style.open : style.close}`}
    >
      <div
        className={style.backdrop}
        onClick={close}
        onTouchEnd={prevent}
        onTouchMove={prevent}
        onTouchStart={close}
        style={css.backDrop}
      ></div>
      <div
        style={css.container}
        className={style.sheet}
        ref={sheetRef}
        onAnimationEnd={onAnimeEnd}
        onTouchStart={touchStart}
        onTouchMove={touchMove}
        onTouchEnd={touchEnd}
      >
        <div className={style.dash} style={css.dash}></div>
        {props.children}
      </div>
    </div>
  );
};
